import { createFileRoute } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { AuthFrame } from '@/components/AuthFrame';
import { PasswordInput } from '@/components/ui/PasswordInput';
import { authClient } from '@/lib/auth-client';
import { Link } from '@/lib/router-compat';

// better-auth validates the mailed link and redirects here with ?token=… (or ?error=INVALID_TOKEN).
export const Route = createFileRoute('/reset-password')({
  validateSearch: z.object({ token: z.string().optional(), error: z.string().optional() }),
  component: ResetPassword
});

function ResetPassword() {
  const { t } = useTranslation();
  const { token, error: linkError } = Route.useSearch();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [state, setState] = useState<'form' | 'done' | 'invalid'>(token && !linkError ? 'form' : 'invalid');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (password.length < 12) return setError(t('auth.passwordTooShort'));
    if (password !== confirm) return setError(t('auth.passwordsDiffer'));
    setBusy(true);
    const { error: err } = await authClient.resetPassword({ newPassword: password, token: token! });
    setBusy(false);
    setState(err ? 'invalid' : 'done');
  };

  return (
    <AuthFrame title={t('auth.resetTitle')}>
      {state === 'invalid' && <div className="alert alert-error alert-soft text-sm">{t('auth.resetInvalid')}</div>}
      {state === 'done' && <div className="alert alert-success alert-soft text-sm">{t('auth.resetDone')}</div>}
      {state === 'form' && (
        <form onSubmit={submit}>
          <PasswordInput label={t('auth.newPassword')} value={password} onChange={setPassword} autoComplete="new-password" hint={t('auth.passwordHint')} autoFocus />
          <PasswordInput label={t('auth.confirmPassword')} value={confirm} onChange={setConfirm} autoComplete="new-password" error={error} />
          <button type="submit" className="btn btn-primary mt-4 w-full" disabled={busy}>
            {busy && <span className="loading loading-spinner loading-sm" />}
            {t('auth.savePassword')}
          </button>
        </form>
      )}
      <Link to={state === 'invalid' ? '/forgot-password' : '/login'} className="btn btn-link btn-sm mt-4 w-full">
        {state === 'invalid' ? t('auth.forgotTitle') : t('auth.backToSignIn')}
      </Link>
    </AuthFrame>
  );
}
